import { useCallback, useEffect, useRef, useState } from "react";
import type { Student } from "../providers/studentsProvider";
import { Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import type { DialogProps } from "../misc/DialogProps";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { runSafelyWithServerClient, type ServerClient } from "../api/server.api";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { createPdfPreview, PdfPreviewDialogContent, type PdfPreview } from "./PdfPreviewDialogContent";
import { Form2 } from "../misc/Form2";
import { BooleanInput } from "../inputs/BooleanInput";
import { Column } from "../misc/Column";
import LoadingButton from "@mui/lab/LoadingButton";

type TeilnahmebescheinigungType =
  | "asf_teilnahmebescheinigung"
  | "b196_teilnahmebescheinigung"
  | "b96_teilnahmebescheinigung";

export function CreateTeilnahmebescheinigungDialog(
  props: {
    student: Student;
    type: TeilnahmebescheinigungType;
    asfCourseId?: string;
  } & DialogProps,
) {
  const { student, type, asfCourseId, ...dialogProps } = props;
  const [preview, setPreview] = useState<undefined | PdfPreview>();
  const { documentName, documentNameWithArticle } = _documentInfo(type);
  const needsForm = type === "b96_teilnahmebescheinigung" || type === "b196_teilnahmebescheinigung";
  const closeDialog = useCallback(() => {
    setPreview(undefined);
    dialogProps.onClose();
  }, [dialogProps.onClose]);

  return (
    <Dialog {...dialogProps} onClose={closeDialog} maxWidth={!preview ? "sm" : "lg"} fullWidth>
      <DialogTitle>{!preview && needsForm ? `${documentName} erstellen` : "Vorschau"}</DialogTitle>
      <DialogCloseButton onClick={closeDialog} />
      {!preview ? (
        needsForm ? (
          <_FormDialogContentAndActions student={student} type={type} setPreview={setPreview} />
        ) : (
          <_LoadingPreviewDialogContent
            student={student}
            type={type}
            asfCourseId={asfCourseId}
            open={dialogProps.open}
            setPreview={setPreview}
            closeDialog={closeDialog}
          />
        )
      ) : (
        <PdfPreviewDialogContent
          student={student}
          preview={preview}
          documentName={documentName}
          documentNameWithArticle={documentNameWithArticle}
          signatureRequestType={type}
          closeDialog={closeDialog}
        />
      )}
    </Dialog>
  );
}

interface _FormValues {
  includePlannedAppointments: boolean;
}

function _FormDialogContentAndActions(props: {
  student: Student;
  type: "b196_teilnahmebescheinigung" | "b96_teilnahmebescheinigung";
  setPreview: (preview: PdfPreview) => void;
}) {
  const { student, type, setPreview } = props;
  const [creatingPreview, setCreatingPreview] = useState(false);
  const { documentNameWithArticle } = _documentInfo(type);

  const createPreview = async (formData: _FormValues) => {
    const { includePlannedAppointments } = formData;
    setCreatingPreview(true);
    await runSafelyWithServerClient(
      async (serverClient) => {
        const preview = await createPdfPreview({
          serverClient,
          createPdf: () =>
            _createTeilnahmebescheinigungPdf(serverClient, {
              studentId: student.id,
              type,
              includePlannedAppointments,
            }),
        });
        setPreview(preview);
      },
      {
        errorMessage: "Fehler beim Erstellen der Vorschau.",
        successMessage: "",
      },
    );
    setCreatingPreview(false);
  };

  return (
    <Form2 onSubmit={createPreview} defaultValues={{ includePlannedAppointments: false }}>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          {`Du kannst hier ${documentNameWithArticle} für ${student.callName} ohne Unter\u00ADschrift erstellen und zur Unter\u00ADschriften\u00ADmappe des Fahrschul\u00ADleiters hinzufügen.`}
        </Alert>
        <Column sx={{ gap: 2 }}>
          <BooleanInput source="includePlannedAppointments" label="Inkl. geplanter Termine" />
        </Column>
      </DialogContent>
      <DialogActions>
        <LoadingButton variant="contained" loading={creatingPreview} type="submit">
          Vorschau
        </LoadingButton>
      </DialogActions>
    </Form2>
  );
}

function _LoadingPreviewDialogContent(props: {
  student: Student;
  type: TeilnahmebescheinigungType;
  asfCourseId?: string;
  open: boolean;
  setPreview: (preview: PdfPreview) => void;
  closeDialog: () => void;
}) {
  const { student, type, asfCourseId, open, setPreview, closeDialog } = props;
  const [creatingPreview, setCreatingPreview] = useState(false);
  const creatingPreviewRef = useRef(false);

  const createPreview = useCallback(async () => {
    setCreatingPreview(true);
    const preview = await runSafelyWithServerClient(
      async (serverClient) => {
        switch (type) {
          case "asf_teilnahmebescheinigung": {
            if (!asfCourseId) {
              throw new Error("asfCourseId not set");
            }
            return createPdfPreview({
              serverClient,
              createPdf: () =>
                serverClient.createASFTeilnahmebescheinigung({ params: { studentId: student.id, asfCourseId } }),
            });
          }
          case "b196_teilnahmebescheinigung":
          case "b96_teilnahmebescheinigung":
            throw new Error(`${type} should be created via the form dialog`);
        }
      },
      {
        errorMessage: "Fehler beim Erstellen der Vorschau.",
        successMessage: "",
      },
    );
    setCreatingPreview(false);
    return preview;
  }, [asfCourseId, student.id, type]);

  useEffect(() => {
    if (!open || creatingPreviewRef.current) {
      return;
    }
    creatingPreviewRef.current = true;
    let active = true;
    void createPreview().then((preview) => {
      if (!active) {
        return;
      }
      if (!preview) {
        closeDialog();
        return;
      }
      setPreview(preview);
    });
    return () => {
      active = false;
    };
  }, [closeDialog, createPreview, open, setPreview]);

  return (
    <DialogContent>
      <Box sx={{ position: "relative", height: "160px" }}>{creatingPreview && <LoadingIndicator />}</Box>
    </DialogContent>
  );
}

function _createTeilnahmebescheinigungPdf(
  serverClient: ServerClient,
  options: {
    studentId: string;
    type: "b196_teilnahmebescheinigung" | "b96_teilnahmebescheinigung";
    includePlannedAppointments: boolean;
  },
) {
  const { studentId, type, includePlannedAppointments } = options;
  const queries = { includePlannedAppointments };
  if (type === "b196_teilnahmebescheinigung") {
    return serverClient.createB196Teilnahmebescheinigung({ params: { studentId }, queries });
  }
  return serverClient.createB96Teilnahmebescheinigung({ params: { studentId }, queries });
}

function _documentInfo(type: TeilnahmebescheinigungType): { documentName: string; documentNameWithArticle: string } {
  switch (type) {
    case "asf_teilnahmebescheinigung":
      return { documentName: "ASF Teilnahmebescheinigung", documentNameWithArticle: "der ASF Teilnahmebescheinigung" };
    case "b196_teilnahmebescheinigung":
      return {
        documentName: "B196 Teilnahmebescheinigung",
        documentNameWithArticle: "der B196 Teilnahmebescheinigung",
      };
    case "b96_teilnahmebescheinigung":
      return {
        documentName: "B96 Teilnahmebescheinigung",
        documentNameWithArticle: "der B96 Teilnahmebescheinigung",
      };
  }
}
